import React, { useState } from 'react'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import Colour from 'components/utils/Colour'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import { LoyaltyTierCard, LoyaltyTierCardHeader, LoyaltyTierCardBody } from 'LuxKit/loyalty/LoyaltyTierCard'
import useSocieteCampaignTargetTier from 'loyalty/hooks/useSocieteCampaignTargetTier'
import TextButton from 'LuxKit/Button/TextButton'
import { rem } from 'polished'
import { useSocieteInterpolate } from 'loyalty/hooks/useSocieteInterpolate'
import BodyText from 'LuxKit/Typography/BodyText'
import useSocieteCampaign from 'loyalty/hooks/useSocieteCampaign'
import { LEBT_SOCIETE_CAMPAIGN_SLUG } from 'loyalty/constants/loyaltyRoutes'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import LoyaltyBenefitIcon from 'loyalty/components/LoyaltyBenefitIcon'
import LoyaltyBenefitIconContainer from 'LuxKit/loyalty/LoyaltyBenefitIconContainer'
import { formatBenefitLabel } from 'loyalty/utils/loyaltyBenefitLabels'
import TextLink from 'LuxKit/TextLink'

const MAX_BENEFITS_COLLAPSED = 3

interface Props {
  onDismiss: () => void
}

function SocieteCompletionCard({ onDismiss }: Props) {
  const campaign = useSocieteCampaign(LEBT_SOCIETE_CAMPAIGN_SLUG)
  const { targetTier, targetTierDisplayName } = useSocieteCampaignTargetTier()
  const interpolate = useSocieteInterpolate()
  const [loyaltyConfig] = useLoyaltyConfig()
  const [isExpanded, setIsExpanded] = useState(false)

  if (!campaign || !loyaltyConfig) return null

  const targetTierBenefits = loyaltyConfig.regionTierBenefits[targetTier]
  const visibleBenefits = isExpanded ? targetTierBenefits : targetTierBenefits.slice(0, MAX_BENEFITS_COLLAPSED)
  const hasMore = targetTierBenefits.length > MAX_BENEFITS_COLLAPSED && !isExpanded

  const headingPrefix = interpolate(campaign.notification.completion.title)
  const highlightText = `${targetTierDisplayName} status`

  return (
    <LoyaltyTierCard rounded>
      <LoyaltyTierCardHeader tier={targetTier} padding={`${rem(32)} ${rem(28)}`}>
        <VerticalSpacer gap={24}>
          <Group direction="horizontal" verticalAlign="start" gap={4}>
            <LuxuryEscapesSmallLogo height={21} fill="currentColor" />
            <LoyaltyProgramNameLogo width={84} height={19} />
          </Group>
          <Heading variant="heading2" style={{ fontWeight: 450 }}>
            {headingPrefix}{' '}
            <Colour value={`tier-${targetTier}-light-contrast`}>{highlightText}</Colour>
          </Heading>
        </VerticalSpacer>
      </LoyaltyTierCardHeader>
      <LoyaltyTierCardBody padding={`${rem(24)}`} gap={20}>
        <BodyText variant="large">
          {interpolate(campaign.notification.completion.subtitle)}
        </BodyText>
        <VerticalSpacer gap={8}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: rem(4) }}>
            {visibleBenefits.map(benefit => {
              const label = formatBenefitLabel(benefit)
              if (!label) return null
              return <div key={benefit.code} style={{ display: 'flex', alignItems: 'center', gap: rem(8) }}>
                <LoyaltyBenefitIconContainer className={`tier-${targetTier}`}>
                  <LoyaltyBenefitIcon benefitType={benefit.code} />
                </LoyaltyBenefitIconContainer>
                <BodyText variant="medium" colour="neutral-two">{label}</BodyText>
              </div>
            })}
            {hasMore && <TextLink size="medium" weight="bold" onClick={() => setIsExpanded(true)}>
              + more
            </TextLink>}
          </div>
        </VerticalSpacer>
      </LoyaltyTierCardBody>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: rem(8), padding: `${rem(16)} ${rem(24)}` }}>
        <TextButton kind="tertiary" size="large" onClick={onDismiss}>
          Maybe later
        </TextButton>
        <TextButton kind="primary" size="large" to="/societe" onClick={onDismiss}>
          Learn more
        </TextButton>
      </div>
    </LoyaltyTierCard>
  )
}

export default SocieteCompletionCard
